// Printed-sheet layouts: per-sheet box styling (tone, icon, num, etc.) + rows of boxes.
// `cols` entries can be a field id or an array of ids (stacked in one column). `widths` uses a 12-column grid.
export const sheetLayouts = {
  'brain-unload': {
    fields: { mind: { tone: 'white', headIcon: 'Brain', twoCol: false } },
    layout: [{ cols: ['mind'] }, { cols: ['next'] }],
  },
  fishbone: {
    fields: { focus: { tone: 'plum', headIcon: 'Fish' }, bones: { bare: true } },
    layout: [{ cols: ['focus'] }, { cols: ['bones'] }, { cols: ['next'] }],
  },
  'thought-sorter': {
    fields: {
      sort: {
        bare: true,
        titles: [
          { title: 'Do', sub: 'Needs my attention now.', tone: 'green', icon: 'CheckCircle2' },
          { title: 'Plan', sub: 'Important, but not today.', tone: 'blue', icon: 'CalendarDays' },
          { title: 'Park', sub: 'Ideas for another day.', tone: 'plum', icon: 'Pin' },
          { title: 'Release', sub: 'Not mine, not necessary, or not worth my energy.', tone: 'orange', icon: 'Leaf' },
        ],
      },
    },
    layout: [{ cols: ['sort'] }, { cols: ['next'] }],
  },
  'starting-ladder': {
    fields: {
      ladder: { tone: 'blue', headIcon: 'Mountain', rungs: true },
      stuck: { tone: 'white', headIcon: 'Target' },
      easier: { tone: 'green', headIcon: 'Sparkles' },
    },
    layout: [{ cols: ['ladder', ['stuck', 'easier']], widths: [5, 7] }, { cols: ['next'] }],
  },
  'blocking-me': {
    fields: { blocks: { tone: 'white', headIcon: 'CheckCircle2', inner: false } },
    layout: [{ cols: ['blocks'] }, { cols: ['next'] }],
  },
  'parking-lot': {
    fields: {
      lot: { tone: 'white', headIcon: 'CircleParking' },
      focus: { tone: 'plum', headIcon: 'Target' },
      window: { tone: 'green', headIcon: 'Timer', timer: true },
      checkin: { tone: 'orange', headIcon: 'Flag' },
      quote: { banner: true, icon: 'Lightbulb' },
    },
    layout: [{ cols: ['lot', ['focus', 'window', 'checkin']], widths: [5, 7] }, { cols: ['quote'] }, { cols: ['next'] }],
  },
  'one-thing-bubble': {
    fields: {
      around: {
        bare: true,
        titles: [
          { title: 'My brain distractions', sub: 'Thoughts, reminders, ideas and temptations — park them here for now.', tone: 'green' },
          { title: 'Other things popping up', sub: 'More thoughts, tabs, “shoulds,” or rabbit holes — they can wait.', tone: 'green' },
        ],
      },
      one: { tone: 'white', headIcon: 'CheckSquare', center: true, bubble: true },
      window: { tone: 'blue', headIcon: 'Timer', timer: true },
      return: { tone: 'orange', headIcon: 'MapPin' },
      quote: { banner: true, icon: 'Heart' },
    },
    layout: [{ cols: ['one'] }, { cols: ['around'] }, { cols: ['window', 'return'] }, { cols: ['quote'] }, { cols: ['next'] }],
  },
  'now-next-later': {
    fields: {
      dump: { tone: 'plum', headIcon: 'CloudDownload', twoCol: true },
      plan: {
        bare: true,
        titles: [
          { title: 'Now', sub: 'Do today. Keep it small and doable.', tone: 'green', icon: 'Target' },
          { title: 'Next', sub: 'Soon, but not today. Plan it in.', tone: 'blue', icon: 'CalendarDays' },
          { title: 'Later', sub: 'Good ideas, someday, or when I have more capacity.', tone: 'orange', icon: 'Sprout' },
        ],
      },
      tip: { banner: true, icon: 'Lightbulb' },
    },
    layout: [{ cols: ['dump'] }, { cols: ['plan'] }, { cols: ['tip'] }, { cols: ['next'] }],
  },
  'reverse-planning': {
    fields: {
      goal: { tone: 'green', headIcon: 'Target' },
      steps: { tone: 'plum', headIcon: 'Route', rungs: true },
      first: { tone: 'blue', headIcon: 'CalendarDays' },
      when: { tone: 'orange', headIcon: 'Timer' },
      by: { tone: 'orange', headIcon: 'CalendarDays' },
      next: { label: 'My commitment' },
    },
    layout: [{ cols: ['goal'] }, { cols: ['steps'] }, { cols: ['first', ['when', 'by']] }, { cols: ['next'] }],
  },
  'time-reality': {
    fields: {
      tasks: { tone: 'plum', headIcon: 'ClipboardList' },
      snapshot: { tone: 'white', headIcon: 'CalendarDays' },
      plan: { tone: 'plum', headIcon: 'Target' },
      remember: { tone: 'green', icon: 'Flower2' },
    },
    layout: [{ cols: ['tasks'] }, { cols: ['snapshot', ['plan', 'remember']] }, { cols: ['next'] }],
  },
  'time-blocking': {
    fields: {
      top3: { tone: 'plum', headIcon: 'Star' },
      blocks: { tone: 'white', headIcon: 'Clock' },
      ingredients: { tone: 'green', headIcon: 'ListChecks' },
      flex: { tone: 'blue', headIcon: 'Sparkles' },
      tips: { tone: 'orange', headIcon: 'Lightbulb' },
      quote: { banner: true, icon: 'Flower2' },
    },
    layout: [{ cols: ['top3', 'blocks'], widths: [5, 7] }, { cols: ['ingredients', ['flex', 'tips']] }, { cols: ['quote'] }, { cols: ['next'] }],
  },
  'inspiration-map': {
    fields: {
      sparks: { tone: 'green', headIcon: 'Sparkles' },
      focus: { tone: 'plum', headIcon: 'Sprout' },
      why: { tone: 'orange', headIcon: 'Target' },
      plan: {
        bare: true,
        titles: [
          { title: 'First steps', sub: '(this week)', tone: 'plum', num: 1 },
          { title: 'Next steps', sub: '(next 2–4 weeks)', tone: 'plum', num: 2 },
          { title: 'The bigger picture', sub: '(down the road)', tone: 'plum', num: 3 },
        ],
      },
      bumps: { tone: 'blue', headIcon: 'Mountain' },
      supports: { tone: 'green', headIcon: 'Heart' },
      quote: { banner: true, icon: 'Flower2' },
    },
    layout: [{ cols: ['sparks', ['focus', 'why']] }, { cols: ['plan'] }, { cols: ['bumps', 'supports'] }, { cols: ['quote'] }, { cols: ['next'] }],
  },
  'energy-reset': {
    fields: {
      level: { tone: 'green', headIcon: 'Gauge' },
      menu: { tone: 'plum', headIcon: 'Lightbulb', optCols: 2 },
      energy: {
        bare: true,
        titles: [
          { title: 'My energy boosters', sub: 'The things that reliably lift my energy and mood.', tone: 'green', icon: 'Leaf' },
          { title: 'My energy drains', sub: 'The things that tend to zap my energy (so I can be more mindful of them).', tone: 'orange', icon: 'Zap' },
        ],
      },
      quote: { banner: true, icon: 'Flower2' },
    },
    layout: [{ cols: ['level', 'menu'], widths: [4, 8] }, { cols: ['energy'] }, { cols: ['quote'] }, { cols: ['next'] }],
  },
  'idea-incubator': {
    fields: {
      dump: { tone: 'plum', headIcon: 'Cloud', twoCol: true, star: false, hint: 'Get it all out! Big, small, wild or “someday” ideas — they all count.' },
      top: { tone: 'green', headIcon: 'Star' },
      explore: { tone: 'green', headIcon: 'Sprout' },
      reality: { tone: 'plum', headIcon: 'Binoculars' },
      park: { tone: 'orange', headIcon: 'Mailbox' },
    },
    extra: [{ after: 'dump', field: { type: 'checklist', id: 'top', label: 'My top ideas', hint: 'Circle or tick the ideas that feel most exciting right now.', labels: Array(5).fill('') } }],
    layout: [{ cols: ['dump'] }, { cols: ['top', 'explore'], widths: [5, 7] }, { cols: ['reality', 'park'] }, { cols: ['next'] }],
  },
  'idea-to-action': {
    fields: {
      idea: { tone: 'plum', headIcon: 'Lightbulb' },
      intention: { tone: 'green', headIcon: 'Target' },
      steps: { tone: 'blue', headIcon: 'ListChecks' },
      real: { tone: 'orange', headIcon: 'Binoculars' },
      timeline: { tone: 'green', headIcon: 'CalendarDays' },
      support: { tone: 'plum', headIcon: 'Heart' },
      quote: { banner: true, icon: 'Sparkles' },
    },
    layout: [{ cols: ['idea', 'intention'] }, { cols: ['steps', 'real'] }, { cols: ['timeline', 'support'] }, { cols: ['quote'] }, { cols: ['next'] }],
  },
};

export const applyLayout = (sheet) => {
  const cfg = sheetLayouts[sheet.id];
  if (!cfg) return sheet;
  let fields = sheet.fields.map((f) => ({ ...f, ...(cfg.fields?.[f.id] || {}) }));
  (cfg.extra || []).forEach(({ after, field }) => {
    const i = fields.findIndex((f) => f.id === after);
    fields.splice(i + 1, 0, { ...field, ...(cfg.fields?.[field.id] || {}) });
  });
  return { ...sheet, fields, layout: cfg.layout };
};
